// Shapes shared by the website, the admin dashboard and server/index.mjs.

export type LeadStatus = 'new' | 'contacted' | 'interested' | 'member' | 'not_interested';
export type Interest = 'course' | 'horo' | 'practice';
export type Level = 'basic' | 'mid' | 'adv';
export type CourseStatus = 'open' | 'waitlist' | 'closed';
export type Verdict = 'good' | 'bad' | 'neutral';

export interface Lead {
  id: string; name: string; phone: string; line: string; email: string;
  interests: Interest[]; status: LeadStatus; source: 'website' | 'manual' | 'register';
  notes: string; consent: boolean; createdAt: string; updatedAt: string; demo?: boolean; memberId?: string;
  enrollments?: Enrollment[];
}

export type EnrollStatus = 'interested' | 'enrolled' | 'completed' | 'cancelled';
export interface Enrollment { courseId: string; status: EnrollStatus; at: string; title?: string; published?: boolean }
/** A lead the team still has to call about: brand-new, or waiting on a course they asked about. */
export const needsFollowUp = (l: { status: string; enrollments?: Enrollment[] }) => l.status === 'new' || (l.enrollments ?? []).some((e) => e.status === 'interested');
export const ENROLL_LABEL: Record<EnrollStatus, string> = { interested: 'สนใจ', enrolled: 'ลงทะเบียนเรียนแล้ว', completed: 'เรียนจบ', cancelled: 'ยกเลิก' };

/** What the website knows about the logged-in member. */
export interface PublicMember { id: string; name: string; email: string; phone: string; line: string; interests: Interest[]; createdAt: string }
/** What the admin sees (never the password hash). */
export interface Member extends PublicMember { status: 'active' | 'suspended'; lastLoginAt: string | null }

export const SOURCE_LABEL: Record<Lead['source'], string> = { website: 'ฟอร์มบนเว็บ', manual: 'บันทึกเอง', register: 'สมัครสมาชิก' };

export interface Course {
  id: string; level: Level; title: string; desc: string; lessons: number; hours: number;
  price: number; status: CourseStatus; published?: boolean; demo?: boolean; updatedAt?: string;
  cover?: string; detail?: string; audience?: string; syllabus?: string[];
}

export interface Move { planet: string; sign: string }
export interface HoroEntry { date: string; enters: Move[]; leaves: Move[]; verdict: Verdict; note: string; demo?: boolean }
export interface Privacy { title: string; body: string; updatedAt: string; demo?: boolean }
export interface Settings { horoscopeDemoPublic: boolean }

export type ClipCat = 'trade' | 'horo' | 'live';
export type ArticleCat = 'horo' | 'psychology' | 'risk' | 'trade';
export interface Clip { id: string; title: string; desc: string; category: ClipCat; url: string; thumb: string; date: string; membersOnly: boolean; published?: boolean; demo?: boolean; locked?: boolean; updatedAt?: string }
export interface Article { id: string; title: string; category: ArticleCat; cover: string; excerpt: string; body: string; date: string; membersOnly: boolean; published?: boolean; demo?: boolean; locked?: boolean; updatedAt?: string }
export interface NewsItem { id: string; title: string; body: string; cover: string; date: string; pinned: boolean; published?: boolean; demo?: boolean; updatedAt?: string }
export interface PageText { title: string; subtitle: string; body: string; cover: string; updatedAt?: string }
export type PageKey = 'home' | 'courses' | 'horoscope' | 'black-trade' | 'demo-trade' | 'clips' | 'articles' | 'news' | 'member';

/** Everything the website gets from GET /api/public/content. */
export interface SiteContent {
  courses: Course[]; clips: Clip[]; articles: Article[]; news: NewsItem[];
  pages: Partial<Record<PageKey, PageText>>; privacy: Privacy | null; lineAddUrl: string;
}

export interface IntegrationLog { at: string; target: 'sheets' | 'line'; event: string; ok: boolean; status: number; detail: string }

export interface Db {
  settings: Settings & { lineAddUrl?: string }; leads: Lead[]; members: Member[]; courses: Course[]; horoscope: HoroEntry[]; privacy: Privacy;
  clips: Clip[]; articles: Article[]; news: NewsItem[]; pages: Record<PageKey, PageText>; integrationLog: IntegrationLog[];
}

export const CLIP_CAT_LABEL: Record<ClipCat, string> = { trade: 'สอนเทรด', horo: 'ดวงการเทรด', live: 'ไลฟ์ย้อนหลัง' };
export const ARTICLE_CAT_LABEL: Record<ArticleCat, string> = { trade: 'การเทรด', psychology: 'จิตวิทยาการเทรด', risk: 'การบริหารความเสี่ยง', horo: 'ดวงการเทรด' };
export const PAGE_LABEL: Record<PageKey, string> = {
  home: 'หน้าหลัก', courses: 'คอร์สเทรด', horoscope: 'เช็กดวงเทรด', 'black-trade': 'Black Trade', 'demo-trade': 'Demo Trade',
  clips: 'คลังวิดีโอ', articles: 'บทความ', news: 'ข่าวสาร', member: 'สมาชิก',
};

/** YouTube video id from watch / youtu.be / shorts / embed links, else null. */
export function youtubeId(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\.|^m\./, '');
    let id: string | null = null;
    if (host === 'youtu.be') id = u.pathname.slice(1);
    else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
      id = u.searchParams.get('v') || (/^\/(?:shorts|embed|live)\/([^/?#]+)/.exec(u.pathname)?.[1] ?? null);
    }
    return id && /^[A-Za-z0-9_-]{6,20}$/.test(id) ? id : null;
  } catch {
    return null;
  }
}

export const LEAD_STATUS_LABEL: Record<LeadStatus, string> = {
  new: 'ใหม่', contacted: 'ติดต่อแล้ว', interested: 'สนใจ', member: 'สมัครแล้ว', not_interested: 'ไม่สนใจ',
};
export const INTEREST_LABEL: Record<Interest, string> = { course: 'คอร์สสอนเทรด', horo: 'เช็กดวงการเทรด', practice: 'Black / Demo Trade' };
export const LEVEL_LABEL: Record<Level, string> = { basic: 'พื้นฐาน', mid: 'ขั้นกลาง', adv: 'ขั้นสูง' };
export const LEVEL_NUM: Record<Level, string> = { basic: 'I', mid: 'II', adv: 'III' };
export const COURSE_STATUS_LABEL: Record<CourseStatus, string> = { open: 'เปิดรับสมัคร', waitlist: 'ลงชื่อรอเปิด', closed: 'ปิดรับสมัคร' };
export const VERDICT_LABEL: Record<Verdict, string> = { good: 'เหมาะแก่การเทรด', bad: 'ไม่เหมาะแก่การเทรด', neutral: 'กลางๆ' };

export const thb = (n: number) => `฿${n.toLocaleString('th-TH')}`;
export const pad2 = (n: number) => String(n).padStart(2, '0');
export const toISODate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
export const thaiDate = (iso: string, weekday = true) =>
  new Intl.DateTimeFormat('th-TH', { ...(weekday ? { weekday: 'long' as const } : {}), day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(`${iso}T00:00:00`));
